import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Calendar, Host, TeamEventMeetingType } from '@vendasta/meetings';
import { combineLatest, Observable, of } from 'rxjs';
import {
  catchError,
  filter,
  first,
  map,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import { BookMeNowStoreService } from '../book-me-now-store.service';
import { TimeSlot } from '../bookme/bookme';

type ScheduledWith = Pick<Calendar, 'displayName' | 'displayProfileUrl'> &
  Pick<Host, 'displayName' | 'displayProfileUrl'>;

@Component({
  selector: 'app-bookme-confirmation',
  templateUrl: './bookme-confirmation.component.html',
  styleUrls: ['./bookme-confirmation.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class BookmeConfirmationComponent implements OnInit {
  meetingCanceled$!: Observable<boolean>;
  meetingRescheduled$!: Observable<boolean>;
  message$!: Observable<string>;
  scheduledWith$!: Observable<ScheduledWith>;
  timezoneOffset$!: Observable<string>;
  timeSlot$!: Observable<TimeSlot>;
  location$!: Observable<string>;
  initialPath: string;
  displayName$!: Observable<string>;

  ctaSmbBetaAccess = 'cta-smb-beta-access';

  constructor(
    private readonly bookmenowService: BookMeNowStoreService,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    const service = this.bookmenowService;

    // Check if we have URL parameters (page refresh scenario)
    const queryParams = this.route.snapshot.queryParams;
    const routeParams = this.route.snapshot.params;
    const meetingId = routeParams.meetingId || queryParams.meetingId;
    const action = queryParams.action || 'book';

    // Check if we have a shortened URL
    // For reschedule/cancel: only action in query params, meetingId in route
    // For book: meetingId and action in query params, no startTime/hostName
    const isShortenedUrl =
      meetingId &&
      action &&
      !queryParams.startTime &&
      !queryParams.hostName &&
      (routeParams.meetingId || (queryParams.meetingId && action === 'book'));

    // Check if we have full URL params (backward compatibility)
    const hasFullUrlParams =
      queryParams.action && (queryParams.startTime || queryParams.hostName);

    if (isShortenedUrl) {
      // Wait for calendar to load, then check if we have state data
      service.calendar$
        .pipe(
          filter<Calendar>((cal) => Boolean(cal)),
          first()
        )
        .subscribe((calendar) => {
          const updatedState = service.getCurrentState();
          const hasStateData =
            updatedState.completedMeetingAction === action &&
            updatedState.calendar &&
            (action === 'cancel' || updatedState.selectedTimeSlot); // Cancel doesn't need timeSlot

          if (hasStateData) {
            // We have the data in state from the booking/reschedule/cancel flow, no need to fetch
            // Ensure calendar is set in state
            if (!updatedState.calendar) {
              service.setState({
                ...updatedState,
                calendar,
              });
            }
          } else {
            // Page refresh scenario - fetch details from API
            this.loadMeetingInfoFromBackend(meetingId, action);
          }
        });
    } else if (hasFullUrlParams) {
      // Restore state from full URL parameters (backward compatibility)
      this.restoreStateFromUrlParams({ ...queryParams, meetingId });
    }

    // Initialize observables (always, regardless of URL type)
    this.meetingCanceled$ = service.canceled$;
    this.meetingRescheduled$ = service.rescheduled$;
    this.message$ = service.confirmationMessage$;
    this.location$ = service.meetingLocation$;
    this.timezoneOffset$ = service.timezoneOffset$;

    // For reschedule, force getting the current value synchronously to ensure we have the latest
    const currentTimeSlot = service.getSelectedTimeSlot();

    if (currentTimeSlot) {
      this.timeSlot$ = of(currentTimeSlot);
    } else {
      this.timeSlot$ = service.timeSlot$;
    }

    this.initialPath = service.getInitialPath();

    this.scheduledWith$ = combineLatest([
      service.calendar$,
      service.completedMeetingHost$,
    ]).pipe(map(([calendar, host]) => host || calendar));

    this.displayName$ = combineLatest([
      service.teamEventMeetingType$,
      service.teamMemberList$,
      service.calendar$,
    ]).pipe(
      map(([meetingType, memberList, calendar]) => {
        const isMultiHost = meetingType === TeamEventMeetingType.MULTI_HOST;
        const nameString = isMultiHost
          ? (memberList ?? []).map((m) => m.displayName).join(', ')
          : calendar?.displayName ?? '';

        return nameString;
      })
    );
  }

  onBackButtonClick() {
    if (this.initialPath) {
      this.router.navigateByUrl(this.initialPath);
    }
  }

  private restoreStateFromUrlParams(params: Record<string, string>): void {
    const service = this.bookmenowService;
    const currentState = service.getCurrentState();

    // Create time slot from URL parameters
    const timeSlot =
      params.startTime && params.endTime
        ? {
            start: new Date(params.startTime),
            end: new Date(params.endTime),
            timezone: params.timezone
              ? {
                  zone: params.timezone,
                  region: params.timezoneRegion || '',
                }
              : null,
          }
        : null;

    // Create calendar/host object from URL parameters
    const calendar = params.hostName
      ? {
          displayName: params.hostName,
          displayProfileUrl:
            params.hostProfileUrl || './assets/images/profile.svg',
        }
      : null;

    // Parse meeting type safely
    let meetingType = TeamEventMeetingType.ROUND_ROBIN;
    if (params.meetingType) {
      try {
        meetingType = params.meetingType as unknown as TeamEventMeetingType;
      } catch {
        meetingType = TeamEventMeetingType.ROUND_ROBIN;
      }
    }

    // Update the service state with restored data
    service.setState({
      ...currentState,
      completedMeetingAction:
        (params.action as 'book' | 'reschedule' | 'cancel') || 'book',
      completedMeetingMessage: params.message || '',
      completedMeetingHost: calendar
        ? ({
            ...calendar,
            displayLogoUrl:
              calendar.displayProfileUrl || './assets/images/profile.svg',
          } as unknown as Host)
        : null,
      calendar: calendar
        ? ({
            ...calendar,
            id: '',
            hostUserIds: [],
            bookingUrl: '',
            displayLogoUrl:
              calendar.displayProfileUrl || './assets/images/profile.svg',
            slug: '',
            externalId: '',
            creatorUserId: '',
            applicationContext: {},
            calendarType: 0,
            encodedApplicationContext: '',
          } as unknown as Calendar)
        : null,
      selectedTimeSlot: timeSlot,
      meetingLocation: params.location || '',
      teamEventMeetingType: meetingType,
    });
  }

  private loadMeetingInfoFromBackend(meetingId: string, action: string): void {
    const service = this.bookmenowService;

    // BookmeComponent already loads the calendar automatically when it sees calendarId in route
    // So we just wait for it to be loaded instead of loading it again
    // We need calendar.id which is the authToken
    service.calendar$
      .pipe(
        filter<Calendar>((cal) => Boolean(cal)), // Wait until calendar is loaded
        first(), // Take the first emission
        switchMap((calendar) => {
          // Use calendar.id as authToken for the API call
          const authToken = calendar.id;
          // getBookedMeetingInfo sets state and returns the meeting info
          return service
            .getBookedMeetingInfo(meetingId, authToken)
            .pipe(map((meetingInfo) => ({ calendar, meetingInfo })));
        }),
        take(1),
        tap(({ calendar, meetingInfo }) => {
          // The service.getBookedMeetingInfo already set the meeting data in state
          const updatedState = service.getCurrentState();

          // Determine confirmation message based on action
          let confirmationMessage = '';
          if (action === 'book') {
            confirmationMessage = $localize`:@@BOOK.CALENDAR_INVITE:A calendar invite has been sent to your email address.`;
          } else if (action === 'cancel') {
            confirmationMessage = $localize`:@@CANCEL.MEETING_CANCELED:Your meeting has been canceled`;
          } else if (action === 'reschedule') {
            confirmationMessage = $localize`:@@RESCHEDULE.MEETING_RESCHEDULED:Your meeting has been rescheduled`;
          }

          // Update state with confirmation action and calendar
          service.setState({
            ...updatedState,
            completedMeetingAction: action as 'book' | 'reschedule' | 'cancel',
            completedMeetingMessage: confirmationMessage,
            completedMeetingHost: {
              displayName:
                meetingInfo.displayName ||
                updatedState.displayName ||
                calendar.displayName,
              displayProfileUrl:
                calendar.displayProfileUrl || './assets/images/profile.svg',
            } as unknown as Host,
            calendar,
            teamEventMeetingType:
              meetingInfo.meetingType ||
              updatedState.teamEventMeetingType ||
              TeamEventMeetingType.ROUND_ROBIN,
          });
        }),
        catchError((err) => {
          console.error('Failed to load meeting info:', err);
          service.setErrorDisplay('error-lookup');
          return of(null);
        })
      )
      .subscribe();
  }
}
